import { isEmpty } from '@jjj/components';
import actions from './actions';
import { createReducer } from '../../../helpers/reducer';
import { keyBy } from '../../../helpers/core';
import { t } from '../../../i18n';
import { isPostalCode } from '../../../helpers/countriesHelpers';

const initState = {
  invoiceLineItems: [],
  requiredAttentionItems: undefined,
  isInvoiceLineItemsLoading: false,
  isRequiredAttentionLoading: false,
  isHoldInvoiceLineItemsLoading: false,
  isUnHoldInvoiceLineItemsLoading: false,
  isConsolidatedInvoicesLoading: false,
  consolidatedInvoices: [],
  isPayConsolidatedInvoicesLoading: false,
  isPayPartiallyConsolidatedInvoicesLoading: false,
  isMarkAsPendingConsolidatedInvoicesLoading: false,
  createConsolidatedInvoice: [],
  isCreateConsolidatedInvoiceFormDataLoading: false,
  editForm: null,
  isCreateConsolidatedInvoiceLoading: false,
  isShowConsolidatedInvoiceLoading: false,
  consolidatedInvoice: [],
  createConsolidatedInvoiceError: true,
};

function validate(attr, value) {
  let error = '';

  if (attr === 'organization_bill_to') {
    if (isEmpty(value)) {
      error = t('dash.validation.required_error', {
        fieldName: t('dash.agencies.invoices.createInvoiceModal.form.bill_to_label'),
      });
    }
  }

  if (attr === 'organization_address') {
    if (isEmpty(value)) {
      error = t('dash.validation.required_error', {
        fieldName: t('dash.agencies.invoices.createInvoiceModal.form.address_label'),
      });
    }
  }

  if (attr === 'organization_zip') {
    if (!isPostalCode(value)) {
      error = t('dash.validation.valid_error', {
        fieldName: t('dash.common.location.postal_code'),
      });
    }

    if (isEmpty(value)) {
      error = t('dash.validation.required_error', {
        fieldName: t('dash.agencies.invoices.createInvoiceModal.form.zip_label'),
      });
    }
  }

  if (attr === 'payment_terms') {
    if (isEmpty(value)) {
      error = t('dash.validation.required_error', {
        fieldName: t('dash.agencies.invoices.createInvoiceModal.form.payment_terms_label'),
      });
    }
  }

  return error;
}

function onLoadInvoiceLineItemsRequest(state) {
  return {
    ...state,
    isInvoiceLineItemsLoading: true,
  };
}

function onLoadInvoiceLineItemsSuccess(state, { payload }) {
  return {
    ...state,
    invoiceLineItems: payload,
    isInvoiceLineItemsLoading: false,
  };
}

function onLoadInvoiceLineItemsError(state) {
  return {
    ...state,
    isInvoiceLineItemsLoading: false,
  };
}

function onHoldInvoiceLineItemsRequest(state) {
  return {
    ...state,
    isHoldInvoiceLineItemsLoading: true,
  };
}

function onHoldInvoiceLineItemsSuccess(state) {
  return {
    ...state,
    isHoldInvoiceLineItemsLoading: false,
  };
}

function onHoldInvoiceLineItemsError(state) {
  return {
    ...state,
    isHoldInvoiceLineItemsLoading: false,
  };
}

function onUnHoldInvoiceLineItemsRequest(state) {
  return {
    ...state,
    isUnHoldInvoiceLineItemsLoading: true,
  };
}

function onUnHoldInvoiceLineItemsSuccess(state) {
  return {
    ...state,
    isUnHoldInvoiceLineItemsLoading: false,
  };
}

function onUnHoldInvoiceLineItemsError(state) {
  return {
    ...state,
    isUnHoldInvoiceLineItemsLoading: false,
  };
}

function onLoadConsolidatedInvoicesRequest(state) {
  return {
    ...state,
    isConsolidatedInvoicesLoading: true,
    isRequiredAttentionLoading: true,
  };
}

function onLoadConsolidatedInvoicesSuccess(state, { payload }) {
  return {
    ...state,
    consolidatedInvoices: !payload?.isAttentionRequired ? payload : state.consolidatedInvoices,
    requiredAttentionItems: payload?.isAttentionRequired ? payload : state.requiredAttentionItems,
    isConsolidatedInvoicesLoading: false,
    isRequiredAttentionLoading: false,
  };
}

function onLoadConsolidatedInvoicesError(state) {
  return {
    ...state,
    isConsolidatedInvoicesLoading: false,
  };
}

function onPayConsolidatedInvoicesRequest(state) {
  return {
    ...state,
    isPayConsolidatedInvoicesLoading: true,
  };
}

function onPayConsolidatedInvoicesSuccess(state) {
  return {
    ...state,
    isPayConsolidatedInvoicesLoading: false,
  };
}

function onPayConsolidatedInvoicesError(state) {
  return {
    ...state,
    isPayConsolidatedInvoicesLoading: false,
  };
}

function onPayPartiallyConsolidatedInvoicesRequest(state) {
  return {
    ...state,
    isPayPartiallyConsolidatedInvoicesLoading: true,
  };
}

function onPayPartiallyConsolidatedInvoicesSuccess(state) {
  return {
    ...state,
    isPayPartiallyConsolidatedInvoicesLoading: false,
  };
}

function onPayPartiallyConsolidatedInvoicesError(state) {
  return {
    ...state,
    isPayPartiallyConsolidatedInvoicesLoading: false,
  };
}

function onMarkAsPendingConsolidatedInvoicesRequest(state) {
  return {
    ...state,
    isMarkAsPendingConsolidatedInvoicesLoading: true,
  };
}

function onMarkAsPendingConsolidatedInvoicesSuccess(state) {
  return {
    ...state,
    isMarkAsPendingConsolidatedInvoicesLoading: false,
  };
}

function onMarkAsPendingConsolidatedInvoicesError(state) {
  return {
    ...state,
    isMarkAsPendingConsolidatedInvoicesLoading: false,
  };
}

function onLoadCreateConsolidatedInvoiceRequest(state) {
  return {
    ...state,
    isCreateConsolidatedInvoiceFormDataLoading: true,
  };
}

function onLoadCreateConsolidatedInvoiceSuccess(state, { payload }) {
  const { organization } = payload || {};
  const editFormObject = {
    organization_bill_to: organization?.name || '',
    organization_address: organization?.address || '',
    organization_zip: organization?.zip || '',
    payment_terms: null,
  };

  const editFormPropArray = Object.keys(editFormObject);
  const editForm = keyBy(
    editFormPropArray,
    prop => prop,
    prop => ({
      name: prop,
      value: editFormObject[prop],
      error: validate(prop, editFormObject[prop]),
      touched: false,
    }),
  );

  return {
    ...state,
    editForm,
    isCreateConsolidatedInvoiceFormDataLoading: false,
    createConsolidatedInvoice: payload,
  };
}

function onLoadCreateConsolidatedInvoiceError(state) {
  return {
    ...state,
    isCreateConsolidatedInvoiceFormDataLoading: false,
  };
}

function onCreateConsolidatedInvoiceRequest(state) {
  return {
    ...state,
    isCreateConsolidatedInvoiceLoading: true,
  };
}

function onCreateConsolidatedInvoiceSuccess(state) {
  return {
    ...state,
    isCreateConsolidatedInvoiceLoading: false,
  };
}

function onCreateConsolidatedInvoiceError(state) {
  return {
    ...state,
    isCreateConsolidatedInvoiceLoading: false,
    createConsolidatedInvoiceError: true,
  };
}

function onShowConsolidatedInvoiceRequest(state) {
  return {
    ...state,
    isShowConsolidatedInvoiceLoading: true,
  };
}

function onShowConsolidatedInvoiceSuccess(state, { payload }) {
  return {
    ...state,
    isShowConsolidatedInvoiceLoading: false,
    consolidatedInvoice: payload,
  };
}

function onShowConsolidatedInvoiceError(state) {
  return {
    ...state,
    isShowConsolidatedInvoiceLoading: false,
  };
}

function onClearStore() {
  return {
    ...initState,
  };
}

function onClearConsolidatedInvoicesModal(state) {
  return {
    ...state,
    consolidatedInvoice: [],
  };
}

function onClearInvoiceLineItemsModal(state) {
  return {
    ...state,
    createConsolidatedInvoice: [],
    editForm: null,
  };
}

function onClearCreateConsolidatedInvoiceError(state) {
  return {
    ...state,
    createConsolidatedInvoiceError: false,
  };
}

function onChangeProperty(state, { payload }) {
  const { attr, value } = payload;
  const { editForm } = state;

  editForm[attr] = {
    value,
    error: validate(attr, value),
    name: attr,
    touched: true,
  };

  return {
    ...state,
    editForm: {
      ...editForm,
    },
  };
}

function onTouch(state, { payload }) {
  const propsArray = Object.entries(payload.editForm);
  const editForm = keyBy(
    propsArray,
    entry => entry[0],
    entry => {
      const [, value] = entry;

      return {
        ...value,
        touched: true,
      };
    },
  );

  return {
    ...state,
    editForm,
  };
}

export default createReducer(initState, {
  [actions.LOAD_INVOICE_LINE_ITEMS_REQUEST]: onLoadInvoiceLineItemsRequest,
  [actions.LOAD_INVOICE_LINE_ITEMS_SUCCESS]: onLoadInvoiceLineItemsSuccess,
  [actions.LOAD_INVOICE_LINE_ITEMS_ERROR]: onLoadInvoiceLineItemsError,
  [actions.HOLD_INVOICE_LINE_ITEMS_REQUEST]: onHoldInvoiceLineItemsRequest,
  [actions.HOLD_INVOICE_LINE_ITEMS_SUCCESS]: onHoldInvoiceLineItemsSuccess,
  [actions.HOLD_INVOICE_LINE_ITEMS_ERROR]: onHoldInvoiceLineItemsError,
  [actions.UN_HOLD_INVOICE_LINE_ITEMS_REQUEST]: onUnHoldInvoiceLineItemsRequest,
  [actions.UN_HOLD_INVOICE_LINE_ITEMS_SUCCESS]: onUnHoldInvoiceLineItemsSuccess,
  [actions.UN_HOLD_INVOICE_LINE_ITEMS_ERROR]: onUnHoldInvoiceLineItemsError,
  [actions.LOAD_CONSOLIDATED_INVOICES_REQUEST]: onLoadConsolidatedInvoicesRequest,
  [actions.LOAD_CONSOLIDATED_INVOICES_SUCCESS]: onLoadConsolidatedInvoicesSuccess,
  [actions.LOAD_CONSOLIDATED_INVOICES_ERROR]: onLoadConsolidatedInvoicesError,
  [actions.PAY_CONSOLIDATED_INVOICES_REQUEST]: onPayConsolidatedInvoicesRequest,
  [actions.PAY_CONSOLIDATED_INVOICES_SUCCESS]: onPayConsolidatedInvoicesSuccess,
  [actions.PAY_CONSOLIDATED_INVOICES_ERROR]: onPayConsolidatedInvoicesError,
  [actions.PAY_PARTIALLY_CONSOLIDATED_INVOICES_REQUEST]: onPayPartiallyConsolidatedInvoicesRequest,
  [actions.PAY_PARTIALLY_CONSOLIDATED_INVOICES_SUCCESS]: onPayPartiallyConsolidatedInvoicesSuccess,
  [actions.PAY_PARTIALLY_CONSOLIDATED_INVOICES_ERROR]: onPayPartiallyConsolidatedInvoicesError,
  [actions.MARK_AS_PENDING_CONSOLIDATED_INVOICES_REQUEST]: onMarkAsPendingConsolidatedInvoicesRequest,
  [actions.MARK_AS_PENDING_CONSOLIDATED_INVOICES_SUCCESS]: onMarkAsPendingConsolidatedInvoicesSuccess,
  [actions.MARK_AS_PENDING_CONSOLIDATED_INVOICES_ERROR]: onMarkAsPendingConsolidatedInvoicesError,
  [actions.LOAD_CREATE_CONSOLIDATED_INVOICE_REQUEST]: onLoadCreateConsolidatedInvoiceRequest,
  [actions.LOAD_CREATE_CONSOLIDATED_INVOICE_SUCCESS]: onLoadCreateConsolidatedInvoiceSuccess,
  [actions.LOAD_CREATE_CONSOLIDATED_INVOICE_ERROR]: onLoadCreateConsolidatedInvoiceError,
  [actions.CREATE_CONSOLIDATED_INVOICE_REQUEST]: onCreateConsolidatedInvoiceRequest,
  [actions.CREATE_CONSOLIDATED_INVOICE_SUCCESS]: onCreateConsolidatedInvoiceSuccess,
  [actions.CREATE_CONSOLIDATED_INVOICE_ERROR]: onCreateConsolidatedInvoiceError,
  [actions.SHOW_CONSOLIDATED_INVOICE_REQUEST]: onShowConsolidatedInvoiceRequest,
  [actions.SHOW_CONSOLIDATED_INVOICE_SUCCESS]: onShowConsolidatedInvoiceSuccess,
  [actions.SHOW_CONSOLIDATED_INVOICE_ERROR]: onShowConsolidatedInvoiceError,
  [actions.CHANGE_PROPERTY]: onChangeProperty,
  [actions.CLEAR_STORE]: onClearStore,
  [actions.CLEAR_CONSOLIDATED_INVOICES_MODAL]: onClearConsolidatedInvoicesModal,
  [actions.CLEAR_INVOICE_LINE_ITEMS_MODAL]: onClearInvoiceLineItemsModal,
  [actions.CLEAR_CREATE_CONSOLIDATED_INVOICE_ERROR]: onClearCreateConsolidatedInvoiceError,
  [actions.TOUCH]: onTouch,
});
